<template>
  <v-sheet class="my-3 mx-auto" rounded outlined width="700">
    <v-stepper v-model="step" alt-labels>
      <v-stepper-header>
        <v-stepper-step :complete="step > 1" step="1" :color="account.color_background"><span class="body-2">Plan</span></v-stepper-step>
        <v-divider />
        <v-stepper-step :complete="step > 2" step="2" :color="account.color_background"><span class="body-2">Facturación</span></v-stepper-step>
        <v-divider />
        <v-stepper-step step="3" :color="account.color_background"><span class="body-2">Pago</span></v-stepper-step>
      </v-stepper-header>
      <v-stepper-items>
        <!-- products -->
        <v-stepper-content step="1">

          <!-- frequencies -->
          <v-row no-gutters>
            <v-col cols="12">
              <v-card class="overflow-hidden mb-4" v-if="availableFrequencies.length > 1" outlined>
                <v-tabs :color="account.color_background" grow height="42" slider-size="3">
                  <template v-for="(f, i) in availableFrequencies">
                    <v-tab class="text-none font-weight-medium" :key="`${i}-tab`" @click="activeFrequency=f.id" :ripple="false">{{f.name | capitalize}}</v-tab>
                    <v-divider v-if="i < (availableFrequencies.length - 1)" :key="`${i}-divider`" vertical />
                  </template>
                </v-tabs>
              </v-card>
            </v-col>
          </v-row>
          <!-- end frequencies -->

          <!-- plans -->
          <v-row no-gutters>
            <v-col cols="12">
              <template v-for="(p, i) in formData.available_plans">
                <v-sheet class="overflow-hidden rounded mb-4" v-if="(p.plan.frequency.id || p.plan.frequency) === activeFrequency" :key="i" outlined>
                  <v-checkbox class="px-3 py-0 my-0" :color="account.color_background" hide-details>
                    <template v-slot:label>
                      <v-row class="pl-1" align="center" justify="space-between">
                        <v-col>
                          <span class="d-block mb-1 body-2 text--secondary font-weight-medium">
                            {{p.name}}
                            <v-tooltip max-width="320px" bottom transition="scale-transition">
                              <template v-slot:activator="{on}">
                                <v-icon v-on="on" small color="secondary">mdi-information</v-icon>
                              </template>
                              <plan-detail :line="p" :currency="currency" />
                            </v-tooltip>
                          </span>
                          <span class="d-block">
                            <span class="mr-1 body-2"><template v-if="p.plan.tiers.length">Desde </template>{{p.plan.amount | currency(p.plan.currency.id)}}</span>
                            <span class="mr-1 caption text--primary">{{p.plan.currency.id}}</span>
                          </span>
                        </v-col>
                        <v-col cols="2" v-if="p.allow_quantity">
                          <v-btn class="text--primary font-weight-regular" outlined :ripple="false" small>
                            1 unidades(s)<v-icon right small>mdi-chevron-down</v-icon>
                          </v-btn>
                        </v-col>
                        <v-col lg="4" sm="5" class="text-right">
                          <span class="body-2 text--secondary font-weight-medium">{{p.plan.amount | currency(p.plan.currency.id)}}</span>
                          <span class="ml-1 caption text--primary">{{p.plan.currency.id}} / {{p.plan.frequency.id || p.plan.frequency | frequency}}</span>
                        </v-col>
                      </v-row>
                    </template>
                  </v-checkbox>
                </v-sheet>
              </template>
            </v-col>
          </v-row>
          <template v-if="!formData.available_plans.length">
            <v-row no-gutters>
              <v-col cols="12">
                <v-sheet class="v-sheet--outlined--dashed text-center py-6" width="100%" outlined rounded>
                  <v-icon class="d-block" color="secondary" size="56">mdi-cash-multiple</v-icon>
                  <span>Selecciona los planes disponibles para este formulario desde el menú del lado izquierdo.</span>
                </v-sheet>
              </v-col>
            </v-row>
          </template>
          <!-- end plans -->

          <!-- addons -->
          <v-row no-gutters v-if="formData.available_addons.length">
            <v-col cols="12">
              <v-toolbar class="mt-4 mb-3" color="transparent" flat height="30">
                <v-toolbar-title class="ml-n4 text--secondary subtitle-1 font-weight-medium">Complementos de la suscripción <v-chip v-if="!formHasMandatoryAddons" class="ml-3" dark small label :color="account.color_background">Opcional</v-chip></v-toolbar-title>
              </v-toolbar>
            </v-col>
            <v-col cols="12">
              <v-sheet class="overflow-hidden rounded mb-4" v-for="a in formData.available_addons" :key="a.name" outlined>
                <v-checkbox class="px-3 py-0 my-0" v-model="a.is_mandatory" :disabled="a.is_mandatory" :color="account.color_background" hide-details>
                  <template v-slot:label>
                    <v-row class="pl-1" align="center" justify="space-between">
                      <v-col class="text-truncate">
                        <span class="d-block mb-1 body-2 text--secondary font-weight-medium">
                          {{a.name}}
                          <v-tooltip max-width="320px" bottom transition="scale-transition">
                            <template v-slot:activator="{on}">
                              <v-icon v-on="on" small color="secondary">mdi-information</v-icon>
                            </template>
                            <span class="d-block text--primary">{{a.description}}</span>
                          </v-tooltip>
                        </span>
                        <span class="d-block">
                          <span class="body-2">{{a.addon.amount | currency(a.addon.currency.id)}}</span>
                          <span class="ml-1 caption text--primary">{{a.addon.currency.id}}</span>
                          <span class="ml-1 caption text--primary">
                            <template v-if="a.addon.apply_scheme === 'one_time'">el primer {{activeFrequency | frequency('unit')}}</template>
                            <template v-else>{{a.addon.currency.id | frequency}} por {{a.addon.duration_times}} {{activeFrequency | frequency('plural')}}</template>
                          </span>
                        </span>
                      </v-col>
                      <v-col cols="2" v-if="a.allow_quantity">
                        <v-btn class="text--primary font-weight-regular" outlined :ripple="false" small>
                          {{a.quantity}}<v-icon right small>mdi-chevron-down</v-icon>
                        </v-btn>
                      </v-col>
                      <v-col lg="4" sm="5" class="text-right">
                        <span class="body-2 text--secondary font-weight-medium">{{a.addon.amount | currency(a.addon.currency.id)}}</span>
                        <span class="ml-1 caption text--primary">{{a.addon.currency.id}}</span>
                      </v-col>
                    </v-row>
                  </template>
                </v-checkbox>
              </v-sheet>
            </v-col>
          </v-row>

          <v-row align="center" justify="space-between" class="mt-6 px-3">
            <v-spacer />
            <v-btn :color="account.color_background" dark height="38" :ripple="false">Facturación<v-icon right small>mdi-arrow-right</v-icon></v-btn>
          </v-row>
        </v-stepper-content>
        <!-- end products -->

        <!-- billing -->
        <v-stepper-content step="2">
          <v-row dense>
            <v-col cols="12">
              <v-text-field :label="formData.custom_labels[0].label || 'Nombre'" :color="account.color_background" background-color="white" required persistent-hint dense outlined maxlength="100" autocomplete="new-password" hide-details />
            </v-col>
            <v-col cols="12">
              <v-text-field id="email" label="Email de facturación" :color="account.color_background" persistent-hint dense outlined background-color="white" maxlength="64" hide-details />
            </v-col>
            <v-col cols="12">
              <v-text-field label="Dirección" :color="account.color_background" background-color="white" required persistent-hint dense outlined maxlength="64" hide-details />
            </v-col>
            <v-col cols="6">
              <v-autocomplete label="País" no-data-text="Se muestran los países habilitados" :menu-props="{ overflowY: true, transition: 'slide-y-transition' }" height="30" background-color="white" autocomplete="new-password" persistent-hint dense outlined hide-details />
            </v-col>
            <v-col cols="6">
              <v-autocomplete :label="formData.custom_labels[1].label || 'Estado'" no-data-text="Se muestran los estados del país seleccionado por el cliente" :menu-props="{ overflowY: true, transition: 'slide-y-transition', maxHeight: '180' }" height="30" background-color="white" autocomplete="new-password" persistent-hint dense outlined hide-details />
            </v-col>
            <v-col cols="6">
              <v-select label="Tipo de factura" no-data-text="Se muestran los documentos habilitados" :menu-props="{ overflowY: true, transition: 'slide-y-transition' }" persistent-hint dense outlined height="30" background-color="white" autocomplete="new-password" hide-details />
            </v-col>
            <v-col cols="6">
              <v-text-field :label="formData.custom_labels[2].label || 'ID Fiscal'" :color="account.color_background" persistent-hint dense outlined background-color="white" autocomplete="new-password" hide-details>
                <template v-slot:append>
                  <v-tooltip max-width="280px" transition="scale-transition" left>
                    <template v-slot:activator="{on}">
                      <v-icon class="py-1" v-on="on" small color="secondary">mdi-information</v-icon>
                    </template>
                    <span>También llamado RUT, VAT o Tax ID</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <v-row align="center" justify="space-between" class="mt-6 px-3">
            <v-btn height="38" outlined :ripple="false"><v-icon left small>mdi-arrow-left</v-icon>Plan</v-btn>
            <v-spacer />
            <v-btn :color="account.color_background" dark height="38" :ripple="false">Pago<v-icon right small>mdi-arrow-right</v-icon></v-btn>
          </v-row>
        </v-stepper-content>
        <!-- end billing -->

        <!-- pay -->
        <v-stepper-content step="3">
          <v-row no-gutters>
            <v-col cols="12">
              <v-card outlined rounded :ripple="false">
                <v-card-text class="py-6">
                  <v-row align="center" no-gutters>
                    <v-col cols="7">
                      <span class="text--secondary body-2 font-weight-medium">Resumen de la suscripción</span>
                    </v-col>
                    <v-col cols="5" class="text-right">
                      <span class="text--secondary body-2 font-weight-medium">•••.••</span>
                      <span class="ml-1 text--primary body-2 font-weight-regular">{{currency}} / {{activeFrequency | frequency}}</span>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col cols="12">
              <v-toolbar class="mt-3" color="transparent" flat height="55">
                <v-toolbar-title class="ml-n4 text--secondary subtitle-1 font-weight-medium">Método de pago</v-toolbar-title>
                <v-spacer />
                <v-toolbar-items>
                  <v-row align="center" justify="end">
                    <v-btn v-if="formData.require_source" @click="toggleSource=!toggleSource" color="primary" :ripple="false"><v-icon>mdi-eye-off</v-icon></v-btn>
                  </v-row>
                </v-toolbar-items>
              </v-toolbar>
            </v-col>
            <v-col cols="12" v-if="formData.require_source">
              <v-sheet v-if="toggleSource" class="py-3 px-9" color="background" min-width="320" max-width="380" rounded outlined>
                <v-row align="end" justify="space-between">
                  <span class="mt-4 subtitle-1 text--secondary font-weight-medium">••• ••• ••• 1234</span>
                </v-row>
                <v-row align="end">
                  <v-col class="px-0" cols="6">
                    <span class="d-block caption">Fecha de expiración</span>
                    <span class="subtitle-1 text--secondary font-weight-medium">12/28</span>
                  </v-col>
                  <v-col class="px-0" cols="6">
                    <span class="d-block caption">CVV</span>
                    <span class="subtitle-1 text--secondary font-weight-medium">•••</span>
                  </v-col>
                </v-row>
                <v-row align="end" justify="space-between">
                  <v-col class="px-0" cols="6">
                    <span class="my-1 py-0 secondary secondary--text">•••••••••••••••••</span>
                    <br />
                    <span class="my-1 py-0 secondary secondary--text">•••••••••••••••••••••••</span>
                  </v-col>
                  <v-col class="px-0 text-right" cols="6">
                    <span><img :src="require(`@/assets/appAsset-brand--Visa.svg`)" alt="Visa" height="24"></span>
                  </v-col>
                </v-row>
              </v-sheet>
              <v-sheet v-else class="v-sheet--outlined--dashed text-center py-6" width="100%" outlined rounded>
                <v-icon class="d-block" color="secondary" size="56">mdi-credit-card-check</v-icon>
                <span>Se muestran los procesadores de pagos conectados y compatibles con pagos recurrentes</span>
                <v-btn :to="{name: 'Integrations'}" target="_blank" color="primary" text :ripple="false">Ver procesadores de pagos conectados<v-icon right small>mdi-open-in-new</v-icon></v-btn>
              </v-sheet>
            </v-col>
            <v-col cols="12" v-else>
              <v-sheet class="pa-6" color="background" rounded>
                <span class="body-2 text--secondary">Te enviaremos la factura junto a un enlace al portal de pagos para que realices el pago manualmente en cada periodo de facturación.</span>
              </v-sheet>
            </v-col>
          </v-row>

          <v-row align="center" justify="space-between" class="mt-6 px-3">
            <v-btn height="38" outlined :ripple="false"><v-icon left small>mdi-arrow-left</v-icon>Facturación</v-btn>
            <v-spacer />
            <v-btn :color="account.color_background" dark height="38" :ripple="false">Suscribir</v-btn>
          </v-row>
        </v-stepper-content>
        <!-- end pay -->
      </v-stepper-items>
    </v-stepper>
  </v-sheet>
</template>
<script>
import GenericViewMixin from '@/mixins/GenericViewMixin'
import PlanDetail from '@/components/signup-forms/PlanDetail'

export default {
  props: {
    currency: {
      type: String,
      required: false,
      default () {
        return 'USD'
      }
    },
    formData: {
      type: Object,
      required: true
    },
    step: {
      type: Number,
      required: true
    }
  },
  components: {
    PlanDetail
  },
  mixins: [
    GenericViewMixin
  ],
  data: () => ({
    activeFrequency: null,
    aggregations: {
      addons: [],
      discount: 0,
      lines: [],
      totals: []
    },
    toggleSource: false
  }),
  watch: {
    'formData.available_plans' () {
      if (this.formData.available_plans.length === 1) {
        this.updateFrequencies()
      }
    }
  },
  mounted () {
    this.updateFrequencies()
  },
  computed: {
    availableFrequencies () {
      if (!this.formData.available_plans.length) {
        return []
      }
      return this.frequencies.filter(f => this.formData.available_plans.some(ap => (ap.plan.frequency.id ?? ap.plan.frequency) === f.id))
    },
    formHasMandatoryAddons () {
      return this.formData.available_addons.some(a => a.is_mandatory)
    }
  },
  methods: {
    updateFrequencies () {
      if (!this.formData.available_plans.length) {
        return false
      }
      const p = this.formData.available_plans[0]
      this.activeFrequency = p.plan.frequency.id ?? p.plan.frequency
    }
  }
}
</script>